import React, { useState, useEffect } from 'react';
import { collection, getDocs } from "firebase/firestore";
import { db } from '../firebase';

// --- INDIVIDUAL POST COMPONENT (Handles local likes & comments) ---
function FeedPost({ post, darkMode }) {
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikesCount(isLiked ? likesCount - 1 : likesCount + 1);
  };

  const handleAddComment = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setComments([...comments, { user: 'You', text: newComment }]);
    setNewComment('');
  };

  return (
    <div className="ig-post" style={{ background: darkMode ? '#1e293b' : 'white', borderColor: darkMode ? '#334155' : '#e2e8f0' }}>
      
      {/* Post Header */}
      <div className="ig-post-header">
        <div className="ig-avatar-wrapper story-active">
          <img src={post.barberImage || 'https://via.placeholder.com/40'} alt={post.barber} />
        </div>
        <div className="ig-post-header-text">
          <h4 style={{ color: darkMode ? 'white' : '#0f172a' }}>{post.barber}</h4>
          <p>📍 {post.shop}</p>
        </div>
        <button className="ig-more-btn" style={{ color: darkMode ? '#cbd5e1' : '#0f172a' }}>⋯</button>
      </div>

      {/* Post Image */}
      <div className="ig-post-image" onDoubleClick={handleLike}>
        <img src={post.image} alt="Haircut showcase" />
      </div>

      {/* Post Actions (Like, Comment, Share) */}
      <div className="ig-post-actions">
        <div className="ig-action-icons">
          <button onClick={handleLike} className="ig-icon-btn" style={{ color: isLiked ? '#ef4444' : (darkMode ? 'white' : '#0f172a') }}>
            {isLiked ? '❤️' : '🤍'}
          </button>
          <button className="ig-icon-btn" style={{ color: darkMode ? 'white' : '#0f172a' }}>💬</button>
          <button className="ig-icon-btn" style={{ color: darkMode ? 'white' : '#0f172a' }}>↗️</button>
        </div>
        <div className="ig-likes-count" style={{ color: darkMode ? 'white' : '#0f172a' }}>
          {likesCount.toLocaleString()} likes
        </div>
      </div>

      {/* Caption & Comments */}
      <div className="ig-post-content">
        <p className="ig-caption" style={{ color: darkMode ? '#cbd5e1' : '#334155' }}>
          <strong style={{ color: darkMode ? 'white' : '#0f172a' }}>{post.barber}</strong> {post.caption}
        </p>
        
        {/* Render existing comments */}
        {comments.map((c, idx) => (
          <p key={idx} className="ig-comment" style={{ color: darkMode ? '#cbd5e1' : '#334155' }}>
            <strong style={{ color: darkMode ? 'white' : '#0f172a' }}>{c.user}</strong> {c.text}
          </p>
        ))}
      </div>

      {/* Add Comment Input */}
      <form className="ig-comment-form" onSubmit={handleAddComment} style={{ borderTop: darkMode ? '1px solid #334155' : '1px solid #e2e8f0' }}>
        <input 
          type="text" 
          placeholder="Add a comment..." 
          value={newComment} 
          onChange={(e) => setNewComment(e.target.value)}
          style={{ color: darkMode ? 'white' : '#0f172a' }}
        />
        <button type="submit" disabled={!newComment.trim()}>Post</button>
      </form>
    </div>
  );
}

// --- MAIN COMMUNITY FEED COMPONENT ---
function SocialFeed({ onNavigate, darkMode }) {
  const [feed, setFeed] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSocialData = async () => {
      try {
        const shopSnap = await getDocs(collection(db, "shops"));
        const allPosts = [];
        const activeStories = [];

        shopSnap.docs.forEach(doc => {
          const shop = doc.data();
          if (!shop.barbers) return;

          shop.barbers.forEach(barber => {
            // 1. Collect Stories
            if (barber.story) {
              activeStories.push({ barber: barber.name, image: barber.image, story: barber.story, shop: shop.shopName });
            }
            // 2. Collect Portfolio Photos for the Feed
            if (barber.portfolio) {
              barber.portfolio.forEach(img => {
                allPosts.push({ 
                  id: Math.random(), 
                  barber: barber.name, 
                  barberImage: barber.image, 
                  shop: shop.shopName, 
                  image: img, 
                  likes: Math.floor(Math.random() * 300) + 20,
                  caption: "Fresh fade of the day! 🔥 Drop a comment if you're booking next."
                });
              });
            }
          });
        });

        // Shuffle posts so the feed feels organic and random
        setFeed(allPosts.sort(() => Math.random() - 0.5));
        setStories(activeStories);
      } catch (error) {
        console.error("Error loading feed:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSocialData();
  }, []);

  return (
    <div style={{ background: darkMode ? '#0f172a' : '#f8fafc', minHeight: '100vh', paddingBottom: '50px' }}>
      
      {/* IG-Style Navbar */}
      <nav style={{ background: darkMode ? '#1e293b' : 'white', padding: '15px 40px', borderBottom: darkMode ? '1px solid #334155' : '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontFamily: 'serif', color: darkMode ? 'white' : '#0f172a' }}>
          BookMyStyle<span style={{color: '#f97316'}}>Community</span>
        </h1>
        <button onClick={() => onNavigate('home')} className="premium-login-btn" style={{ padding: '8px 20px', fontSize: '13px' }}>
          ← Back to Booking
        </button>
      </nav>

      <div className="ig-feed-container">
        
        {/* --- STORIES TRAY --- */}
        {stories.length > 0 && (
          <div className="ig-stories-tray" style={{ background: darkMode ? '#1e293b' : 'white', borderColor: darkMode ? '#334155' : '#e2e8f0' }}>
            {stories.map((s, i) => (
              <div key={i} className="ig-story-item" onClick={() => alert(`STORY FROM ${s.barber}:\n\n"${s.story}"`)}>
                <div className="ig-story-ring">
                  <img src={s.image || 'https://via.placeholder.com/60'} alt="story" style={{ border: darkMode ? '2px solid #1e293b' : '2px solid white' }} />
                </div>
                <span style={{ color: darkMode ? '#cbd5e1' : '#475569' }}>{s.barber.split(' ')[0]}</span>
              </div>
            ))}
          </div>
        )}

        {/* --- MAIN FEED --- */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px', color: '#64748b' }}>Loading Community Feed...</div>
        ) : feed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px', color: '#64748b' }}>
            <h2>No posts yet!</h2>
            <p>Tell your barbers to upload their portfolios to start the feed.</p>
          </div>
        ) : (
          feed.map(post => <FeedPost key={post.id} post={post} darkMode={darkMode} />)
        )}
      </div>
    </div>
  );
}

export default SocialFeed;